import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip, Collapse } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { fetchStops, fetchFormation, Stop, Journey, UnitData, FormationResponse, formatTime } from './analysisApi';
import { fetchModelClass, ModelClassEntry } from './accuracyApi';
import OccupancyChart from './OccupancyChart';
import AccuracyBox from './AccuracyBox';

// ---- Palette ----
const C = {
  bg:             '#111e2d',
  bgRow:          'rgba(255,255,255,0.02)',
  bgRowHighlight: 'rgba(240,165,0,0.07)',
  bgRowCancelled: 'rgba(248,113,113,0.05)',
  bgFormation:    'rgba(255,255,255,0.20)',
  outline:        '#0a0a0a',
  // Coupler is a touch weaker (lighter) than the nose-cone fill #3a3a3a so it links the
  // units without competing with them. Note greys near #4e4e4e are the same luminance as
  // the formation band and disappear entirely — this is about as weak as it can go.
  coupler:        '#404040',
  border:         'rgba(255,255,255,0.06)',
  borderAccent:   'rgba(240,165,0,0.3)',
  text:           'rgba(255,255,255,0.85)',
  textMuted:      'rgba(255,255,255,0.35)',
  textDim:        'rgba(255,255,255,0.2)',
  amber:          '#f0a500',
  green:          '#4ade80',
  red:            '#f87171',
  headerBg:       '#0d1b2a',
};

// ---- Auto-refresh config ----
const POLL_INTERVAL_MS = 15_000;
const POLL_DURATION_MS = 2 * 60_000;

// ---- Occupancy colour scale ----
function occupancyColour(count: number | null): string {
  if (count === null) return 'rgba(150,150,150,0.12)';
  if (count < 0)      return 'rgba(248,113,113,0.35)';
  if (count < 30)     return '#1a6b3a';
  if (count < 80)     return '#7a5c00';
  return '#7a1c1c';
}
function occupancyTextColour(count: number | null): string {
  if (count === null) return 'rgba(150,150,150,0.4)';
  if (count < 0)      return '#f87171';
  if (count < 30)     return '#4ade80';
  if (count < 80)     return '#f0a500';
  return '#fca5a5';
}

function stufeColour(stufe: string | null | undefined): string {
  if (stufe === 'NIEDRIG') return '#4ade80';
  if (stufe === 'MITTEL')  return '#f0a500';
  if (stufe === 'HOCH')    return '#f87171';
  return 'rgba(150,150,150,0.4)';
}

// ---- Unit body geometry (214x25 viewBox) ----
// A unit is a short driver's cab at each end plus four identical car cells filling the
// span between them. Nothing is drawn underneath a cab. (The original art ran the car
// rects 12.36–199.14, overlapping the cabs by ~32%/~28% — that showed up as a shaded
// rectangle over each nose — and its cells were unequal at 46.30/47.09.)
const SVG_W      = 214;
const CAR_TOP    = 1.0874;
const CAR_BOTTOM = 23.7897;
const CAR_H      = CAR_BOTTOM - CAR_TOP;

// The driver's cab is deliberately short relative to a car cell (15 vs ~45.5), and its
// roofline descends continuously from the car join to the nose — there is no flat roof
// section. The original cone was 26.4 long and spent 11.8 of that on a flat top.
const CONE_LEN  = 15;
const CONE_TIP  = 0.9;                  // frontmost x, inset so the 1.5 stroke stays in view
const CONE_BACK = CONE_TIP + CONE_LEN;  // where the cab meets the first car

// Cubic segments of the left cab, front-to-back: roof/windscreen sweep, front face,
// rounded bottom lip. Mirrored via x -> SVG_W - x for the right-hand cab.
const CONE_SEGMENTS: [number, number][][] = [
  [[11.0, 2.2],  [5.5, 7.5],        [2.4, 14.5]],
  [[1.3, 17.2],  [0.9, 20.0],       [0.9, 21.8]],
  [[0.9, 22.9],  [1.7, CAR_BOTTOM], [2.9, CAR_BOTTOM]],
];

function conePath(side: 'left' | 'right'): string {
  const fx = (x: number) => (side === 'left' ? x : SVG_W - x).toFixed(4);
  const head = `M ${fx(CONE_BACK)} ${CAR_TOP}`;
  const curves = CONE_SEGMENTS
    .map(([c1, c2, p]) => `C ${fx(c1[0])} ${c1[1]} ${fx(c2[0])} ${c2[1]} ${fx(p[0])} ${p[1]}`)
    .join(' ');
  return `${head} ${curves} H ${fx(CONE_BACK)} Z`;
}
const CAP_LEFT_D  = conePath('left');
const CAP_RIGHT_D = conePath('right');

const BODY_X0 = CONE_BACK;          // right edge of the left cab
const BODY_X1 = SVG_W - CONE_BACK;  // left edge of the right cab
const CAR_W   = (BODY_X1 - BODY_X0) / 4;
const CAR_CY  = CAR_TOP + CAR_H / 2;
const CARS = [0, 1, 2, 3].map(i => ({
  x:  BODY_X0 + i * CAR_W,
  cx: BODY_X0 + (i + 0.5) * CAR_W,
}));

// Outline weight — heavier than the original 0.943742 so the body reads clearly
// against the lighter formation band.
const STROKE_W = 1.5;

// ---- Model-class chip ----
// The class the forecaster actually delivered for this unit at this stop
// (N+1, read from the prediction archive) — shown next to the message-carried
// value so the two are comparable at a glance.
const ModelChip: React.FC<{ entry: ModelClassEntry }> = ({ entry }) => (
  <Tooltip title={`Modellklasse (Prognosearchiv, N+1): ${entry.cls}${entry.seats != null ? ` · ${entry.seats} Pers.` : ''}`}>
    <Box sx={{
      px: '3px', py: '1px', borderRadius: '2px', lineHeight: 1,
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: `1px solid ${stufeColour(entry.cls)}`,
      display: 'flex', alignItems: 'center', gap: '3px',
    }}>
      <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '8px', fontWeight: 700, color: stufeColour(entry.cls), letterSpacing: '0.05em', lineHeight: 1 }}>
        KI
      </Typography>
      {entry.seats != null && (
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '8px', color: stufeColour(entry.cls), lineHeight: 1 }}>
          {entry.seats}
        </Typography>
      )}
    </Box>
  </Tooltip>
);

// ---- CarriageUnit ----
const CarriageUnit: React.FC<{ unit: UnitData; hasData: boolean; model?: ModelClassEntry | null }> = ({ unit, hasData, model }) => {
  const sw        = STROKE_W;
  const capFill   = hasData ? '#3a3a3a' : '#4a4a4a';
  const capStroke = hasData ? C.outline : 'rgba(220,220,220,0.55)';
  const carStroke = hasData ? C.outline : 'rgba(220,220,220,0.55)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: 16 }}>
        {unit.occupancy_source ? (
          <>
            {unit.vu_belegung != null && (
              <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', fontWeight: 600, color: stufeColour(unit.vu_auslastungsstufe), lineHeight: 1 }}>
                {Math.round(unit.vu_belegung)}
              </Typography>
            )}
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: occupancyTextColour(unit.occupancy_total), lineHeight: 1 }}>
              {unit.vu_belegung != null ? '(' : ''}{unit.occupancy_total !== null ? Math.round(unit.occupancy_total) : '—'}{unit.vu_belegung != null ? ')' : ''}
            </Typography>
            <Box sx={{
              px: '3px', py: '1px', borderRadius: '2px', lineHeight: 1,
              backgroundColor: unit.occupancy_source === 'afz' ? 'rgba(14,122,130,0.35)' : 'rgba(59,79,212,0.35)',
              border: `1px solid ${unit.occupancy_source === 'afz' ? '#14b8c2' : '#7c8ef7'}`,
            }}>
              <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '8px', fontWeight: 700, color: unit.occupancy_source === 'afz' ? '#14b8c2' : '#7c8ef7', letterSpacing: '0.05em', lineHeight: 1 }}>
                {unit.occupancy_source.toUpperCase()}
              </Typography>
            </Box>
            {model && <ModelChip entry={model} />}
          </>
        ) : (
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.textDim, lineHeight: 1 }}>—</Typography>
        )}
      </Box>
      <svg width="214" height="25" viewBox="0 0 214 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d={CAP_LEFT_D}  fill={capFill} stroke={capStroke} strokeWidth={sw}/>
        <path d={CAP_RIGHT_D} fill={capFill} stroke={capStroke} strokeWidth={sw}/>
        {CARS.map(({ x, cx }, i) => {
          const raw     = unit.occupancy_by_carriage[i] ?? null;
          const vuCar   = unit.vu_by_carriage?.[i];
          const vuBel   = vuCar?.belegung ?? null;
          const vuStufe = vuCar?.auslastungsstufe ?? null;

          // Tint alpha is 0.32 rather than 0.18: the formation band behind these cells is
          // now much lighter, and at 0.18 the green/amber/red all composited to near-
          // identical muddy greys.
          const fill = !hasData
            ? 'rgba(120,120,120,0.14)'
            : vuStufe === 'NIEDRIG' ? 'rgba(74,222,128,0.32)'
            : vuStufe === 'MITTEL'  ? 'rgba(240,165,0,0.32)'
            : vuStufe === 'HOCH'    ? 'rgba(248,113,113,0.32)'
            : occupancyColour(raw);

          const tCol = hasData ? stufeColour(vuStufe) : 'rgba(150,150,150,0.4)';

          const label = !hasData
            ? '?'
            : vuBel != null && raw != null
              ? `${Math.round(vuBel)}(${Math.round(raw)})`
              : vuBel != null
                ? String(Math.round(vuBel))
                : raw != null
                  ? String(Math.round(raw))
                  : '?';

          return (
            <g key={i}>
              <rect x={x} y={CAR_TOP} width={CAR_W} height={CAR_H} fill={fill} stroke={carStroke} strokeWidth={sw}/>
              <text x={cx} y={CAR_CY} fontSize="7" fontFamily="IBM Plex Mono, monospace" fontWeight="600"
                fill={tCol} textAnchor="middle" dominantBaseline="middle">
                {label}
              </text>
            </g>
          );
        })}
      </svg>
      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontFamily: '"IBM Plex Mono"', fontWeight: 500, letterSpacing: '0.04em' }}>
        {unit.uic}
      </Typography>
    </Box>
  );
};

// ---- Formation ----
const Formation: React.FC<{
  units: UnitData[] | null;
  hasActualDep: boolean;
  modelFor?: (uic: string) => ModelClassEntry | null;
}> = ({ units, hasActualDep, modelFor }) => {
  const displayUnits: (UnitData | null)[] = units && units.length > 0 ? units : [null, null];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
      {displayUnits.map((unit, i) => (
        <React.Fragment key={unit?.uic ?? i}>
          {unit ? (
            <CarriageUnit unit={unit} hasData={hasActualDep && unit.occupancy_source !== null} model={modelFor ? modelFor(unit.uic) : null} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
              <Box sx={{ height: 16 }} />
              <svg width="214" height="25" viewBox="0 0 214 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d={CAP_LEFT_D}  fill="#4a4a4a" stroke="rgba(220,220,220,0.4)" strokeWidth={STROKE_W}/>
                <path d={CAP_RIGHT_D} fill="#4a4a4a" stroke="rgba(220,220,220,0.4)" strokeWidth={STROKE_W}/>
                {CARS.map(({ x, cx }, ci) => (
                  <g key={ci}>
                    <rect x={x} y={CAR_TOP} width={CAR_W} height={CAR_H} fill="rgba(120,120,120,0.14)" stroke="rgba(220,220,220,0.4)" strokeWidth={STROKE_W}/>
                    <text x={cx} y={CAR_CY} fontSize="8.5" fontFamily="monospace" fill="rgba(235,235,235,0.5)"
                      textAnchor="middle" dominantBaseline="middle">?</text>
                  </g>
                ))}
              </svg>
              <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.1)', fontFamily: '"IBM Plex Mono"' }}>———</Typography>
            </Box>
          )}
          {i < displayUnits.length - 1 && (
            <Box sx={{ width: '7px', height: '9px', mb: '14px', backgroundColor: unit ? C.coupler : 'rgba(220,220,220,0.4)', borderRadius: '1px', flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

// ---- Source badge ----
type Source = 'ris_mgw' | 'ris_journey' | null | undefined;
const SOURCE_STYLE: Record<string, { bg: string; border: string; label: string }> = {
  ris_mgw:     { bg: '#0e7a82', border: '#14b8c2', label: 'M' },
  ris_journey: { bg: '#3b4fd4', border: '#7c8ef7', label: 'J' },
};
const NULL_STYLE = { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.15)' };

const SourceBadge: React.FC<{ source: Source; isFirst?: boolean; isLast?: boolean; kind: 'dep' | 'arr' }> = ({ source, isFirst, isLast, kind }) => {
  if (kind === 'arr' && isFirst) return <Box sx={{ width: 22, height: 22 }} />;
  if (kind === 'dep' && isLast)  return <Box sx={{ width: 22, height: 22 }} />;
  const style = source ? (SOURCE_STYLE[source] ?? null) : null;
  return (
    <Box sx={{ width: 22, height: 22, borderRadius: '4px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: style ? style.bg : NULL_STYLE.bg, border: `1px solid ${style ? style.border : NULL_STYLE.border}` }}>
      {style && (
        <Typography sx={{ fontSize: '11px', fontWeight: 800, fontFamily: '"IBM Plex Mono"', color: '#ffffff', lineHeight: 1, letterSpacing: '-0.02em' }}>
          {style.label}
        </Typography>
      )}
    </Box>
  );
};

// ---- Delay chip ----
const DelayChip: React.FC<{ seconds: number | null }> = ({ seconds }) => {
  if (seconds === null || seconds === undefined) return null;
  const mins   = Math.round(seconds / 60);
  const onTime = Math.abs(mins) <= 1;
  return (
    <Box sx={{ px: '5px', py: '1px', borderRadius: '3px', fontSize: '10px', fontFamily: '"IBM Plex Mono"', color: onTime ? C.green : (mins > 0 ? C.red : C.green), backgroundColor: onTime ? 'rgba(74,222,128,0.08)' : (mins > 0 ? 'rgba(248,113,113,0.1)' : 'rgba(74,222,128,0.08)') }}>
      {onTime ? '±0' : `${mins > 0 ? '+' : ''}${mins}m`}
    </Box>
  );
};

// ---- Column header ----
const ColHeader: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' | 'center'; flex?: number | string }> = ({ children, align = 'left', flex = 1 }) => (
  <Typography sx={{ flex, fontSize: '11px', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', fontFamily: '"IBM Plex Mono"', textAlign: align, fontWeight: 500 }}>
    {children}
  </Typography>
);

// ---- Refresh icon ----
const RefreshIcon: React.FC<{ spinning: boolean }> = ({ spinning }) => (
  <Box component="svg" viewBox="0 0 24 24" fill="none" sx={{ width: 16, height: 16, animation: spinning ? 'spin 0.8s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }}>
    <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

// ---- Countdown timer ----
const CountdownTimer: React.FC<{ expiresAt: number | null; onReset: () => void }> = ({ expiresAt, onReset }) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!expiresAt) { setRemaining(0); return; }
    const tick = () => { const ms = Math.max(0, expiresAt - Date.now()); setRemaining(ms); };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const totalSec = Math.ceil(remaining / 1000);
  const mm       = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss       = String(totalSec % 60).padStart(2, '0');
  const pct      = (remaining / POLL_DURATION_MS) * 100;
  const urgent   = totalSec <= 15;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: 4, py: '5px', backgroundColor: 'rgba(0,0,0,0.2)', borderBottom: `1px solid ${C.border}` }}>
      <Box sx={{ flex: 1, height: '3px', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, backgroundColor: urgent ? C.red : C.amber, opacity: 0.7, transition: 'width 0.25s linear, background-color 0.4s' }} />
      </Box>
      <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', fontWeight: 500, color: urgent ? C.red : C.amber, letterSpacing: '0.08em', minWidth: '36px', textAlign: 'right', transition: 'color 0.4s' }}>
        {mm}:{ss}
      </Typography>
      <Tooltip title="Timer zurücksetzen (2 min)">
        <Box onClick={onReset} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '3px', cursor: 'pointer', color: C.textDim, border: '1px solid rgba(255,255,255,0.12)', '&:hover': { color: C.amber, borderColor: 'rgba(240,165,0,0.4)', backgroundColor: 'rgba(240,165,0,0.06)' }, transition: 'all 0.15s' }}>
          <RefreshIcon spinning={false} />
        </Box>
      </Tooltip>
    </Box>
  );
};

// ---- Helpers ----
function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' });
  } catch { return '—'; }
}

// ============================================================================
// Main component
// ============================================================================
const JourneyView: React.FC<{ journey: Journey }> = ({ journey }) => {
  const [stops,          setStops]          = useState<Stop[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [lastRefreshed,  setLastRefreshed]  = useState<Date | null>(null);
  const [autoActive,     setAutoActive]     = useState(false);
  const [autoExpiresAt,  setAutoExpiresAt]  = useState<number | null>(null);
  const [formationByEva, setFormationByEva] = useState<Record<string, UnitData[]>>({});
  const [modelEntries,   setModelEntries]   = useState<Record<string, ModelClassEntry>>({});

  // ── Occupancy chart toggle ─────────────────────────────────────────────────
  const [showOccupancy, setShowOccupancy] = useState(false);

  const pollTimerRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const expireTimerRef = useRef<ReturnType<typeof setTimeout>  | null>(null);

  // ---- Fetch ----
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else         setRefreshing(true);
    setError(null);
    try {
      const r = await fetchStops(journey.journey_id);
      setStops(r.stops);
      setLastRefreshed(new Date());
      const formationResults = await Promise.allSettled(
        r.stops.map(s => fetchFormation(journey.journey_id, s.stop_eva))
      );
      const byEva: Record<string, UnitData[]> = {};
      formationResults.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value.unit_count > 0) {
          byEva[r.stops[idx].stop_eva] = result.value.units;
        }
      });
      setFormationByEva(byEva);

      // Forecaster-delivered classes (best effort; the view works without them)
      const m = journey.journey_id.match(/^(\d{4})(\d{2})(\d{2})-/);
      if (m) {
        fetchModelClass(`${m[1]}-${m[2]}-${m[3]}`, journey.journey_id)
          .then(mc => setModelEntries(mc.entries || {}))
          .catch(() => setModelEntries({}));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [journey.journey_id]);

  // ---- Auto-refresh ----
  const startAuto = useCallback(() => {
    if (pollTimerRef.current)   clearInterval(pollTimerRef.current);
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
    const expiresAt = Date.now() + POLL_DURATION_MS;
    setAutoActive(true);
    setAutoExpiresAt(expiresAt);
    pollTimerRef.current = setInterval(() => {
      if (Date.now() >= expiresAt) { clearInterval(pollTimerRef.current!); setAutoActive(false); setAutoExpiresAt(null); return; }
      load(true);
    }, POLL_INTERVAL_MS);
    expireTimerRef.current = setTimeout(() => { setAutoActive(false); setAutoExpiresAt(null); }, POLL_DURATION_MS);
  }, [load]);

  const stopAuto = useCallback(() => {
    if (pollTimerRef.current)   clearInterval(pollTimerRef.current);
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
    setAutoActive(false);
    setAutoExpiresAt(null);
  }, []);

  // ---- Journey change ----
  useEffect(() => {
    stopAuto();
    setStops([]);
    setFormationByEva({});
    setModelEntries({});
    setLastRefreshed(null);
    setShowOccupancy(false);   // ← reset chart on journey change
    load(false).then(() => startAuto());
    return () => stopAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey.journey_id]);

  const handleRefresh = useCallback(() => { load(true); startAuto(); }, [load, startAuto]);

  const liveCount = stops.filter(s => s.actual_departure || s.actual_arrival).length;

  const currentSeq = React.useMemo(() => {
    let last = -1;
    stops.forEach(s => { if (s.actual_departure && s.stop_sequence > last) last = s.stop_sequence; });
    return last;
  }, [stops]);

  if (loading) return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
      <CircularProgress sx={{ color: C.amber }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
      <Typography sx={{ color: C.red, fontFamily: '"IBM Plex Sans"', fontSize: '13px' }}>{error}</Typography>
    </Box>
  );

  return (
    <Box sx={{ flex: 1, backgroundColor: C.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      {/* ── Journey header ──────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: C.headerBg, borderBottom: `1px solid ${C.borderAccent}`, px: 4, py: 2, position: 'sticky', top: 0, zIndex: 10, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: '4px' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '20px', fontWeight: 500, color: '#ffffff' }}>{journey.route_id}</Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: C.amber, letterSpacing: '0.1em' }}>{journey.line_id}</Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '13px', color: C.textMuted, flex: 1 }}>
            {formatTime(journey.scheduled_departure)} → {formatTime(journey.scheduled_arrival)}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {[
              { val: liveCount,    label: 'LIVE',  color: liveCount > 0 ? C.green : C.textMuted },
              { val: stops.length, label: 'STOPS', color: C.textMuted },
            ].map(({ val, label, color }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <Box sx={{ width: '1px', height: 28, backgroundColor: 'rgba(255,255,255,0.06)' }} />}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '16px', color, lineHeight: 1 }}>{val}</Typography>
                  <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '9px', color: C.textDim, letterSpacing: '0.1em' }}>{label}</Typography>
                </Box>
              </React.Fragment>
            ))}

            <Box sx={{ width: '1px', height: 28, backgroundColor: 'rgba(255,255,255,0.06)' }} />

            {/* ── Belegungsanalyse toggle ──────────────────────────────────── */}
            <Tooltip title="Belegungsanalyse" placement="bottom">
              <IconButton
                onClick={() => setShowOccupancy(v => !v)}
                size="small"
                sx={{
                  color: showOccupancy ? C.amber : C.textMuted,
                  backgroundColor: showOccupancy ? 'rgba(240,165,0,0.1)' : 'transparent',
                  border: showOccupancy ? `1px solid rgba(240,165,0,0.3)` : '1px solid transparent',
                  borderRadius: '6px',
                  '&:hover': { color: C.amber, backgroundColor: 'rgba(240,165,0,0.08)' },
                }}
              >
                <BarChartIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Box sx={{ width: '1px', height: 28, backgroundColor: 'rgba(255,255,255,0.06)' }} />

            {/* Auto-refresh toggle */}
            <Tooltip title={autoActive ? 'Timer stoppen' : 'Auto-refresh starten (15 s / 2 min)'}>
              <Box onClick={() => startAuto()} sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', px: '8px', py: '4px', borderRadius: '4px', border: `1px solid ${autoActive ? 'rgba(240,165,0,0.4)' : 'rgba(255,255,255,0.1)'}`, backgroundColor: autoActive ? 'rgba(240,165,0,0.08)' : 'transparent', transition: 'all 0.2s', '&:hover': { borderColor: C.amber, backgroundColor: 'rgba(240,165,0,0.08)' } }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: autoActive ? C.amber : 'rgba(255,255,255,0.2)', boxShadow: autoActive ? `0 0 6px ${C.amber}` : 'none', transition: 'all 0.3s' }} />
                <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '9px', letterSpacing: '0.1em', color: autoActive ? C.amber : C.textDim, textTransform: 'uppercase' }}>
                  {autoActive ? 'Reset' : 'Auto'}
                </Typography>
              </Box>
            </Tooltip>

            {/* Manual refresh */}
            <Tooltip title="Jetzt aktualisieren">
              <IconButton onClick={handleRefresh} size="small" sx={{ color: refreshing ? C.amber : C.textMuted, '&:hover': { color: C.amber }, p: '4px' }}>
                <RefreshIcon spinning={refreshing} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.textMuted, letterSpacing: '0.04em', userSelect: 'all' }}>
            {journey.journey_id}
          </Typography>
          {lastRefreshed && (
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.textDim, letterSpacing: '0.04em' }}>
              Aktualisiert: {lastRefreshed.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Countdown timer ─────────────────────────────────────────────────── */}
      <CountdownTimer expiresAt={autoExpiresAt} onReset={startAuto} />

      {/* ── Forecast accuracy (per UIC, this journey) ─────────────────────────
          HIDDEN 2026-09-18. Two reasons, and the first is the decision:

          1. It did not earn its space. A single journey's hit rate over a
             handful of samples says little, and the per-horizon error is now
             visible where it is actually useful — in the OccupancyChart
             tooltip, against the stop you are looking at.

          2. It could not work anyway. The rollup publishes overall and
             per-line only; by_journey is dropped by `slim` and is far too
             large for a Firestore document, so this box was the one consumer
             with no source but the direct API — which needs
             REACT_APP_ACCURACY_API_URL injected at build time.

          Commented out rather than deleted, per the practice with
          DEMO.trainsLive / currentPax / pis: restoring it is uncommenting,
          and leaving it here records why it went.
      <Box sx={{ px: 4, py: '10px', borderBottom: `1px solid ${C.border}`, backgroundColor: 'rgba(0,0,0,0.15)', flexShrink: 0 }}>
        <AccuracyBox
          scope="journey"
          journeyId={journey.journey_id}
          day={journey.service_day}
          title="Prognosegüte — diese Fahrt"
        />
      </Box>
      ─────────────────────────────────────────────────────────────────────── */}

      {/* ── Occupancy chart panel ────────────────────────────────────────────── */}
      <Collapse in={showOccupancy} unmountOnExit>
        <Box sx={{ borderBottom: `1px solid ${C.border}`, px: 2, py: 2, backgroundColor: '#0a1520', flexShrink: 0, maxHeight: '520px', overflowY: 'auto' }}>
          <OccupancyChart journeyId={journey.journey_id} />
        </Box>
      </Collapse>

      {/* ── Scrollable stop list ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Column headers */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 4, py: '8px', borderBottom: `1px solid ${C.border}`, gap: 2, position: 'sticky', top: 0, backgroundColor: C.bg, zIndex: 5 }}>
          <ColHeader flex="0 0 28px"> </ColHeader>
          <ColHeader flex="0 0 22px" align="center">Ab.</ColHeader>
          <ColHeader flex="0 0 22px" align="center">An.</ColHeader>
          <ColHeader flex={3}>Station</ColHeader>
          <ColHeader flex={1} align="right">Soll An.</ColHeader>
          <ColHeader flex={1} align="right">Ist An.</ColHeader>
          <ColHeader flex={1} align="right">Soll Ab.</ColHeader>
          <ColHeader flex={1} align="right">Ist Ab.</ColHeader>
          <ColHeader flex="0 0 48px" align="center">Vsp.</ColHeader>
          <ColHeader flex="0 0 32px" align="center">Gleis</ColHeader>
          <ColHeader flex={2}>Status</ColHeader>
          <ColHeader flex={2}>Notes</ColHeader>
        </Box>

        {/* Stop rows */}
        {stops.map((stop, idx) => {
          const hasActualDep = !!stop.actual_departure;
          const hasActualArr = !!stop.actual_arrival;
          const hasAnyActual = hasActualDep || hasActualArr;
          const isCurrent    = stop.stop_sequence === currentSeq;
          const isCancelled  = stop.cancelled;
          const isFirst      = idx === 0;
          const isLast       = idx === stops.length - 1;
          const delaySeconds = isLast ? (stop.delay_an_seconds ?? null) : (stop.delay_ab_seconds ?? null);
          const vuRaw        = (stop as any).status_vubasis as string | null | undefined;
          const vuParts      = vuRaw ? vuRaw.split(':').map((s: string) => s.trim()) : null;
          const vuMain       = vuParts?.[0] ?? null;
          const vuSub        = vuParts?.slice(1).join(' : ') ?? null;

          return (
            <Box key={stop.stop_sequence} sx={{ borderBottom: `1px solid ${C.border}`, borderLeft: isCurrent ? `3px solid ${C.amber}` : '3px solid transparent', backgroundColor: isCancelled ? C.bgRowCancelled : isCurrent ? C.bgRowHighlight : C.bgRow }}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 4, py: '10px', gap: 2 }}>
                <Typography sx={{ flex: '0 0 28px', fontSize: '11px', color: C.text, fontWeight: 500, fontFamily: '"IBM Plex Mono"', textAlign: 'right' }}>
                  {stop.stop_sequence}
                </Typography>
                <Box sx={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}>
                  <SourceBadge source={(stop as any).actual_departure_source as Source} isFirst={isFirst} isLast={isLast} kind="dep" />
                </Box>
                <Box sx={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}>
                  <SourceBadge source={(stop as any).actual_arrival_source as Source} isFirst={isFirst} isLast={isLast} kind="arr" />
                </Box>
                <Typography sx={{ flex: 3, fontSize: '13px', fontFamily: '"IBM Plex Sans"', color: isCancelled ? C.red : isCurrent ? '#ffffff' : C.text, fontWeight: isCurrent ? 500 : 400, textDecoration: isCancelled ? 'line-through' : 'none' }}>
                  {stop.stop_name}
                  {isCancelled && <Typography component="span" sx={{ fontSize: '10px', ml: 1, color: C.red, fontFamily: '"IBM Plex Mono"' }}>ENTFÄLLT</Typography>}
                </Typography>
                <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: hasAnyActual ? C.text : C.textMuted }}>{isFirst ? '—' : formatTime(stop.scheduled_arrival)}</Typography>
                <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: hasActualArr ? C.green : C.textDim }}>{isFirst ? '—' : fmtTime(stop.actual_arrival)}</Typography>
                <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: hasAnyActual ? C.text : C.textMuted }}>{isLast ? '—' : formatTime(stop.scheduled_departure)}</Typography>
                <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: hasActualDep ? C.green : C.textDim }}>{isLast ? '—' : fmtTime(stop.actual_departure)}</Typography>
                <Box sx={{ flex: '0 0 48px', display: 'flex', justifyContent: 'center' }}>
                  {hasAnyActual && <DelayChip seconds={delaySeconds} />}
                </Box>
                <Typography sx={{ flex: '0 0 32px', fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Mono"', textAlign: 'center' }}>{stop.platform ?? '—'}</Typography>
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'baseline', gap: '4px', overflow: 'hidden' }}>
                  {vuMain ? (
                    <>
                      <Typography sx={{ fontSize: '10px', fontFamily: '"IBM Plex Mono"', color: C.textMuted, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{vuMain}</Typography>
                      {vuSub && <Typography sx={{ fontSize: '9px', fontFamily: '"IBM Plex Mono"', color: C.textDim, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>: {vuSub}</Typography>}
                    </>
                  ) : (
                    <Typography sx={{ fontSize: '10px', fontFamily: '"IBM Plex Mono"', color: 'rgba(255,255,255,0.1)' }}>—</Typography>
                  )}
                </Box>
                <Box sx={{ flex: 2, overflow: 'hidden' }}>
                  {stop.stop_status ? (
                    <Typography sx={{ fontSize: '10px', fontFamily: '"IBM Plex Mono"', color: '#f0a500', backgroundColor: 'rgba(240,165,0,0.08)', border: '1px solid rgba(240,165,0,0.25)', borderRadius: '3px', px: '5px', py: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%', letterSpacing: '0.03em' }}>
                      {stop.stop_status}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: '10px', fontFamily: '"IBM Plex Mono"', color: 'rgba(255,255,255,0.1)' }}>–</Typography>
                  )}
                </Box>
              </Box>

              {/* Formation row */}
              <Box sx={{ px: 4, pb: '10px', pt: '6px', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: C.bgFormation }}>
                <Box sx={{ flex: '0 0 28px' }} />
                <Formation
                  units={formationByEva[stop.stop_eva] ?? null}
                  hasActualDep={hasActualDep}
                  modelFor={(uic) => modelEntries[`${uic}|1|${stop.stop_eva}`] ?? null}
                />
              </Box>
            </Box>
          );
        })}

        {stops.length === 0 && !loading && (
          <Box sx={{ px: 4, pt: 4 }}>
            <Typography sx={{ color: C.textMuted, fontFamily: '"IBM Plex Sans"', fontSize: '13px' }}>Keine Halte gefunden.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JourneyView;
